/**
 * Welcome to the main entry point of the app. In this file, we'll
 * be kicking off our app.
 *
 * Most of this file is boilerplate and you shouldn't need to modify
 * it very often. But take some time to look through and understand
 * what is going on here.
 *
 * The app navigation resides in ./app/navigators, so head over there
 * if you're interested in adding screens and navigators.
 */
import "./i18n"
import "./utils/alert"
import "./utils/dimensions"
import "./utils/ignore-warnings"
import "./utils/moment"
import "./utils/numeral"
import "./utils/yup"
import * as storage from "./utils/storage"
import I18n from "i18n-js"
import React, { FC, useCallback, useEffect, useMemo, useReducer, useState } from "react"
import SystemNavigationBar from "react-native-system-navigation-bar"
import codePush from "react-native-code-push"
import numeral from "numeral"
import { ErrorBoundary } from "./screens"
import {
  ActivityIndicator,
  Image,
  ImageStyle,
  Text as ReactNativeText,
  TextInput as ReactNativeTextInput,
  Platform,
  StyleProp,
  View,
  ViewStyle,
} from "react-native"
import { NAVIGATION_PERSISTENCE_KEY, useNavigationPersistence } from "./navigators"
import { Modal, NativeBaseProvider } from "native-base"
import { ToggleStorybook } from "../storybook/toggle-storybook"
import { color, nativeBaseTheme } from "./theme"
import { connectToDevTools } from "react-devtools-core"
import { gestureHandlerRootHOC } from "react-native-gesture-handler"
import { initFonts } from "./theme/fonts" // expo
import { initialWindowMetrics, SafeAreaProvider } from "react-native-safe-area-context"
import { readRawRootStore, RefreshRootStoreProvider, RootStore, RootStoreProvider, setupRootStore } from "./models"
import { setRootStore as setGlobalRootStore } from "./utils/root-store"
import {
  AutoFeederPondProvider,
  IntercomProvider,
  OneSignal, RouteNameProvider,
  Sentry,
  sentryOptions,
  Settings,
  Text,
  UXCam, uXCamOptions
} from "./components"
import { codePushCallback } from "./utils/code-push"
import config from "./utils/config"
import { activateKeepAwake, deactivateKeepAwake } from "@sayem314/react-native-keep-awake"
import { WalkthroughProvider } from "react-native-walkthrough"
import JalaAnalytic from "./utils/jala-analytic"

export * from "./navigators"

// Ask for consent first if necessary
// Possibly only do this for iOS if no need to handle a GDPR-type flow
if (Platform.OS !== "web") Settings.initializeSDK();

// This puts screens in a native ViewController or Activity. If you want fully native
// stack navigation, use `createNativeStackNavigator` in place of `createStackNavigator`:
// https://github.com/kmagiera/react-native-screens#using-native-stack-navigator

// export const NAVIGATION_PERSISTENCE_KEY = "NAVIGATION_STATE"

// Set default props
(ReactNativeText as any).defaultProps = (ReactNativeText as any).defaultProps || {};
(ReactNativeText as any).defaultProps.allowFontScaling = false;
(ReactNativeText as any).defaultProps.maxFontSizeMultiplier = 1;
(ReactNativeTextInput as any).defaultProps = (ReactNativeTextInput as any).defaultProps || {};
(ReactNativeTextInput as any).defaultProps.allowFontScaling = false;
(ReactNativeTextInput as any).defaultProps.maxFontSizeMultiplier = 1;

if (__DEV__) {
  connectToDevTools({
    host: "localhost",
    port: 8097,
  });
}

export function nativeBaseProviderHOC(WrappedComponent) {
  return function nativeBaseProvider(props) {
    return <NativeBaseProvider theme={nativeBaseTheme}>
      <WrappedComponent {...props}/>
    </NativeBaseProvider>
  }
}

const LOADING: ViewStyle = {
  flex: 1,
  backgroundColor: color.primary,
  alignItems: "center",
  justifyContent: "center"
}

const IMAGE: StyleProp<ImageStyle> = {
  width: 90,
  height: 40
}

const app =  require('./app.png')

const Loading = () => {
  SystemNavigationBar.navigationHide().catch()

  return <View style={LOADING}>
    <Image source={app} style={IMAGE} />
  </View>
}

const CodePushUpdateProgressOverlay: FC = (props) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [progress, setProgress] = useState<string>(null)

  const codePushStatusDidChange = useCallback((status) => {
    switch (status) {
      case codePush.SyncStatus.DOWNLOADING_PACKAGE:
        setLoading(true);
        break;
      case codePush.SyncStatus.INSTALLING_UPDATE:
        setLoading(true);
        break;
      default:
        setLoading(false);
        break;
    }
  }, [])

  const codePushDownloadDidProgress = useCallback((progress) => {
    setProgress(numeral(progress.receivedBytes / progress.totalBytes).format('0,0%'));
  }, [])

  useEffect(() => {
    codePushCallback.syncStatusChangedCallback = codePushStatusDidChange;
    codePushCallback.downloadProgressCallback = codePushDownloadDidProgress;
    return () => {
      codePushCallback.syncStatusChangedCallback = null;
      codePushCallback.downloadProgressCallback = null;
    }
  }, [codePushDownloadDidProgress, codePushStatusDidChange])

  useEffect(() => {
    if (loading) activateKeepAwake()
    else deactivateKeepAwake();
  }, [loading])

  return (
    <Modal testID="jala:id/code-push-overlay" isOpen={loading}>
      <ActivityIndicator color={color.palette.white} />
      {progress && <Text text={progress} preset="body2" paddingTop="8px" colorScheme="dark" />}
    </Modal>
  )
}

/**
 * This is the root component of our app.
 */
function appHOC(WrappedComponent) {
  const {
    APPCENTER_DEPLOYMENT_KEY_ANDROID, APPCENTER_DEPLOYMENT_KEY_IOS,
    INTERCOM_APP_ID,
  } = config()

  function App(props) {
    const [rootStore, setRootStore] = useState<RootStore | undefined>(undefined)
    const [autoFeederPondReducer, setAutoFeederPond] = useReducer(
      useCallback((prevState, action) => ({ autoFeederPond: action }), []),
      { autoFeederPond: null }
    )

    const {
      initialNavigationState,
      onNavigationStateChange,
      isRestored: isNavigationStateRestored,
      previousRouteNameRef, routeNameRef,
      previousRouteName, routeName
    } = useNavigationPersistence(storage, NAVIGATION_PERSISTENCE_KEY)

    // Kick off initial async loading actions, like loading fonts and RootStore
    useEffect(() => {
      ;(async () => {
        await initFonts() // expo
        setupRootStore().then((originalRootStore) => {
          setRootStore(originalRootStore)
        })
      })()
    }, [])

    const refreshRootStore = useCallback(() => {
      return (
        readRawRootStore().then((originalRootStore) => {
          setRootStore(originalRootStore)
          return originalRootStore
        })
      )
    }, [])

    const autoFeederPondValueContext = useMemo(() => ({
      autoFeederPond: autoFeederPondReducer.autoFeederPond,
      setAutoFeederPond
    }), [autoFeederPondReducer])

    // Before we show the app, we have to wait for our state to be ready.
    // In the meantime, don't render anything. This will be the background
    // color set in native by rootView's background color.
    // In iOS: application:didFinishLaunchingWithOptions:
    // In Android: https://stackoverflow.com/a/45838109/204044
    // You can replace with your own loading component if you wish.
    if (!rootStore || !isNavigationStateRestored) return <Loading />

    setGlobalRootStore(rootStore)

    const { appStore } = rootStore

    appStore.setLocale(appStore.locale || I18n.locale)

    OneSignal.init({ appId: "368fb6fe-5509-491e-addd-a8ca7e1bc7f0" })

    UXCam.optIntoSchematicRecordings(); // Add this line to enable iOS screen recordings
    UXCam.startWithConfiguration(uXCamOptions);

    Sentry.init(sentryOptions);

    JalaAnalytic.init({});

    // otherwise, we're ready to render the app
    return (<>
      <ToggleStorybook>
        <RootStoreProvider value={rootStore}>
          <RefreshRootStoreProvider value={refreshRootStore}>
            <SafeAreaProvider initialMetrics={initialWindowMetrics}>
              <AutoFeederPondProvider value={autoFeederPondValueContext}>
                <RouteNameProvider value={{ previousRouteNameRef, routeNameRef, previousRouteName, routeName }}>
                  <WalkthroughProvider>
                    <IntercomProvider appId={INTERCOM_APP_ID}>
                      <ErrorBoundary catchErrors={"always"}>
                        <WrappedComponent initialState={initialNavigationState} onStateChange={onNavigationStateChange} {...props} />
                      </ErrorBoundary>
                    </IntercomProvider>
                  </WalkthroughProvider>
                </RouteNameProvider>
              </AutoFeederPondProvider>
            </SafeAreaProvider>
          </RefreshRootStoreProvider>
        </RootStoreProvider>
      </ToggleStorybook>
      <CodePushUpdateProgressOverlay />
    </>)
  }

  if (Platform.OS === 'web')
    return gestureHandlerRootHOC(nativeBaseProviderHOC(App))

  return codePush({
    deploymentKey: Platform.OS === 'ios' ? APPCENTER_DEPLOYMENT_KEY_IOS : APPCENTER_DEPLOYMENT_KEY_ANDROID,
    updateDialog: null,
    checkFrequency: codePush.CheckFrequency.MANUAL,
    mandatoryInstallMode: codePush.InstallMode.IMMEDIATE,
    installMode: codePush.InstallMode.ON_NEXT_RESUME
  })(gestureHandlerRootHOC(nativeBaseProviderHOC(App)))
}

export default appHOC
